import { createRootRoute, createRoute, redirect } from "@tanstack/react-router";

import { ApiKeysPage } from "@/pages/api-keys";
import { ContactPage } from "@/pages/contact";
import { DashboardPage } from "@/pages/dashboard";
import { DocsPage } from "@/pages/docs";
import { FaqPage } from "@/pages/faq";
import { OnboardingPage } from "@/pages/onboarding";
import { ErrorPage, NotFoundPage, RootComponent } from "@/pages/layout";
import { OrgBillingPage } from "@/pages/org-billing";
import { OrgSettingsPage } from "@/pages/org-settings";
import { PricingPage } from "@/pages/pricing";
import { LandingPage } from "@/pages/root";
import { OrgPage } from "@/pages/org";
import { ProjectPage } from "@/pages/project";
import { SettingsPage } from "@/pages/settings";
import { SignInPage } from "@/pages/sign-in";
import { LogInPage } from "@/pages/log-in";
import { SignUpPage } from "@/pages/sign-up";
import { ConfirmEmailPage } from "@/pages/confirm-email";
import { UserBillingPage } from "@/pages/user-billing";
import { CookiesPage, PrivacyPage, TermsPage } from "@/pages/static-md";
import { BlogIndexPage, BlogPostPage } from "@/pages/blog";
import { InvitationAcceptPage } from "@/pages/invitation-accept";

const rootRoute = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
  errorComponent: ErrorPage,
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: LandingPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  component: DashboardPage,
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  component: OnboardingPage,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  component: SignInPage,
});

const logInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/log-in",
  component: LogInPage,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-up",
  component: SignUpPage,
});

const confirmEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-up/confirm-email",
  component: ConfirmEmailPage,
});

const orgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/orgs/$orgId",
  component: OrgPage,
});

const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/orgs/$orgId/projects/$projectId",
  component: ProjectPage,
});

const userProjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId",
  component: ProjectPage,
});

const orgSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/orgs/$orgId/settings",
  component: OrgSettingsPage,
});

const orgBillingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/orgs/$orgId/billing",
  component: OrgBillingPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: SettingsPage,
});

const apiKeysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/api-keys",
  component: ApiKeysPage,
});

const userBillingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing",
  component: UserBillingPage,
});

const docsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs",
  beforeLoad: () => {
    throw redirect({ to: "/docs/$section", params: { section: "overview" } });
  },
});

const docsSectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs/$section",
  component: DocsPage,
});

const blogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/blog",
  component: BlogIndexPage,
});

const blogPostRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/blog/$slug",
  component: BlogPostPage,
});

const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pricing",
  component: PricingPage,
});

const contactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/contact",
  component: ContactPage,
});

const faqRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/faq",
  component: FaqPage,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: PrivacyPage,
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: TermsPage,
});

const cookiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cookies",
  component: CookiesPage,
});

const invitationAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invitations/accept",
  component: InvitationAcceptPage,
});

export const routeTree = rootRoute.addChildren([
  homeRoute,
  dashboardRoute,
  onboardingRoute,
  signInRoute,
  logInRoute,
  signUpRoute,
  confirmEmailRoute,
  orgRoute,
  orgSettingsRoute,
  orgBillingRoute,
  projectRoute,
  userProjectRoute,
  settingsRoute,
  apiKeysRoute,
  userBillingRoute,
  docsRoute,
  docsSectionRoute,
  blogRoute,
  blogPostRoute,
  pricingRoute,
  contactRoute,
  faqRoute,
  privacyRoute,
  termsRoute,
  cookiesRoute,
  invitationAcceptRoute,
]);
